import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { ImageDropzone, FileUploadButton } from 'react-file-utils';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { compose } from 'recompose';
import {
  DatePicker,
  TimePicker,
} from '@mui/x-date-pickers';
import {
  CircularProgress,
  Alert,
  Checkbox,
  FormHelperText,
} from '@mui/material';

import {
  isToday,
  combineDateAndTimeWithUTCOffset,
} from '../../../../utils/date';
import { getUTCOffset, roundToNext5Minutes } from '../../../../utils/time';
import { attachmentShape } from '../../../../utils/attachments';
import { COACHING_ACTIVITY, Event } from '../../../../utils/events';

import MessageInputContext, { withMessageInputContextProvider } from '../../../../context/MessageInputContext';
import ChatContext from '../../../../context/ChatContext';
import ChatChannelContext from '../../../../context/ChatChannelContext';

import MessageInput from '../../../MessageInput';
import AttachmentsPreview from '../../../ChannelMessageInput/components/AttachmentsPreview';
import LoadingOverlay from '../../../LoadingOverlay/LoadingOverlay';
import TimeCounter from '../../../MessageInput/components/TimeCounter';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import ScheduleQueue from '../ScheduleQueue/ScheduleQueue';

import texts from './texts.json';
import {
  Container,
  StyledText,
  StyledTextField,
  StyledButton,
  StyledFakeButton,
  AttachmentButtonsContainer,
  AttachmentsContainer,
  CenterLoading,
  FileAttachmentIcon,
  StyledMicIcon,
  StyledStopIcon,
  DateSelectorContainer,
  ConfirmButtonsContainer,
  ScheduleButton,
  PickerContainer,
  CheckBoxContainer,
} from './styles';

const QUEUED_MESSAGE_SEND_OFFSET_MS = 1000;

const FormScheduleMessage = ({
  defaultText,
  defaultSendDate,
  defaultSendTime,
  defaultAttachments,
  onSave,
  onCancel,
  saveButtonLabel,
  cancelButtonLabel,
}) => {
  const isComponentMountedRef = useComponentMounted();
  const [text, setText] = useState(defaultText);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldForceSend, setShouldForceSend] = useState(false);
  const lastAutoInsertedSmartContext = useRef('');

  const [attachmentsReady, setAttachmentsReady] = useState(false);

  const {
    attachments,
    onAddAttachments,
    onLoadAttachments,
    onRemoveAttachment,
    isVoiceMemoRecording,
    startVoiceRecording,
    stopVoiceRecording,
    reset,
  } = useContext(MessageInputContext);
  const {
    chatUser,
    authUser,
    logEvent,
  } = useContext(ChatContext);
  const {
    activeChannel,
    coachTimezone,
    smartContextText,
    scheduleQueue,
    handleQueueTextChange,
    setScheduleQueue,
  } = useContext(ChatChannelContext);

  const isQueueMode = scheduleQueue.length > 0;
  const scheduleAllButtonLabel = `${texts.scheduleAll} ${scheduleQueue.length} ${
    scheduleQueue.length === 1 ? texts.messageSingular : texts.messagePlural
  }`;

  const timezoneOffset = getUTCOffset(coachTimezone || moment.tz.guess());

  const [sendDate, setSendDate] = useState(defaultSendDate ?? moment().utcOffset(timezoneOffset));
  const [sendTime, setSendTime] = useState(defaultSendTime ?? roundToNext5Minutes(moment().utcOffset(timezoneOffset)));

  const { showToast } = useToast();
  const currentChannelAttachments = useMemo(() => (
    attachments[activeChannel.id] || []
  ), [
    attachments,
    activeChannel.id,
  ]);

  useEffect(() => {
    if (!attachmentsReady && currentChannelAttachments.length === 0 && isComponentMountedRef.current) {
      onLoadAttachments(defaultAttachments)
        .then(() => setAttachmentsReady(true));
    }
  }, [defaultAttachments,
    onLoadAttachments,
    attachmentsReady,
    currentChannelAttachments.length,
    isComponentMountedRef,
  ]);

  useEffect(() => {
    if (!smartContextText || !isComponentMountedRef.current) {
      return;
    }

    const hasDefaultText = !!defaultText?.trim();
    const hasUserText = !!text.trim();
    const hasNotAutoInserted = lastAutoInsertedSmartContext.current !== smartContextText;

    if (!hasDefaultText && !hasUserText && hasNotAutoInserted) {
      setText(smartContextText);
      lastAutoInsertedSmartContext.current = smartContextText;
    }
  }, [smartContextText, defaultText, text, isComponentMountedRef]);

  const resetFields = useCallback(() => {
    if (isComponentMountedRef.current) {
      setText('');
      setSendDate(moment().local());
      setSendTime(moment().local());
      reset();
    }
  }, [
    reset,
    isComponentMountedRef,
  ]);

  const minTime = useMemo(() => (
    isToday(moment(sendDate).utcOffset(timezoneOffset)) ? moment().utcOffset(timezoneOffset) : null
  ), [
    sendDate,
    timezoneOffset,
  ]);

  const handleTextChange = useCallback((changedText) => {
    setText(changedText);
  }, []);

  const checkDateTime = useCallback(() => {
    const dateTimeIsInvalid = !moment(sendTime, 'HH:mm', true).isValid() || !moment(sendDate).isValid();
    if (dateTimeIsInvalid) {
      showToast(`${texts.invalidDateTime}`, { error: true });
      return false;
    }
    if (parseInt(sendTime.minutes(), 10) % 5 !== 0) {
      showToast(`${texts.inavalidTime}`, { error: true });
      return false;
    }
    return true;
  }, [
    sendTime,
    sendDate,
    showToast,
  ]);

  const computeSendAt = useCallback(() => {
    if (!checkDateTime()) return null;
    const sendAt = combineDateAndTimeWithUTCOffset(sendDate, sendTime, timezoneOffset).toDate();
    if (sendAt < moment().utcOffset(timezoneOffset)) {
      showToast(texts.passedDateTimeWarning, { error: true });
      return null;
    }
    return sendAt;
  }, [checkDateTime, sendDate, sendTime, timezoneOffset, showToast]);

  const handleSave = useCallback(async () => {
    const sendAt = computeSendAt();
    if (!sendAt) return;
    setIsLoading(true);
    await onSave(sendAt, text, currentChannelAttachments, shouldForceSend);
    if (isComponentMountedRef.current) {
      setIsLoading(false);
    }
    logEvent(Event.COACHING_ACTIVITY, {
      actionTaken: COACHING_ACTIVITY.SINGLE_MESSAGE_SCHEDULED,
      sendAt,
      coach: chatUser.id,
      scheduledBy: authUser.id,
      recipient: activeChannel.userId,
    });
    resetFields();
  }, [
    computeSendAt,
    text,
    currentChannelAttachments,
    onSave,
    resetFields,
    isComponentMountedRef,
    logEvent,
    chatUser,
    authUser,
    activeChannel,
    shouldForceSend,
  ]);

  const handleCancel = useCallback(async () => {
    await onCancel();
    resetFields();
  }, [
    onCancel,
    resetFields,
  ]);

  const handleSaveAll = useCallback(async () => {
    const sendAt = computeSendAt();
    if (!sendAt) return;
    setIsLoading(true);
    const failedQueueItems = [];

    try {
      // Save queued messages one at a time, offsetting each send time by one second to preserve order.
      await scheduleQueue.reduce(
        (chain, item, index) => chain.then(() => Promise.resolve()
          .then(() => onSave(
            new Date(sendAt.getTime() + index * QUEUED_MESSAGE_SEND_OFFSET_MS),
            item.text,
            [],
            shouldForceSend,
          ))
          .catch((error) => {
            Sentry.captureException(error, {
              extra: {
                messageId: item.messageId,
                queueIndex: index,
                channelId: activeChannel.id,
                coach: chatUser.id,
                scheduledBy: authUser.id,
              },
            });
            failedQueueItems.push(item);
          })),
        Promise.resolve(),
      );

      if (failedQueueItems.length > 0) {
        showToast(
          `${failedQueueItems.length} of ${scheduleQueue.length} ${texts.queueScheduleFailure}`,
          { error: true },
        );
      }

      if (failedQueueItems.length < scheduleQueue.length) {
        setScheduleQueue(failedQueueItems);
        logEvent(Event.COACHING_ACTIVITY, {
          actionTaken: COACHING_ACTIVITY.QUEUE_SCHEDULED,
          sendAt,
          messageCount: scheduleQueue.length - failedQueueItems.length,
          coach: chatUser.id,
          scheduledBy: authUser.id,
          recipient: activeChannel.userId,
        });
      }

      if (failedQueueItems.length === 0) {
        resetFields();
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    computeSendAt,
    scheduleQueue,
    onSave,
    shouldForceSend,
    showToast,
    setScheduleQueue,
    isComponentMountedRef,
    resetFields,
    logEvent,
    activeChannel.id,
    activeChannel.userId,
    chatUser.id,
    authUser.id,
  ]);

  return (
    <Container>
      {isQueueMode ? (
        <ScheduleQueue items={scheduleQueue} onTextChange={handleQueueTextChange} />
      ) : (
        <>
          <StyledText>
            {texts.messageLabel}
          </StyledText>
          <MessageInput
            text={text}
            onChange={handleTextChange}
            isEditMessage
            placeholder={texts.enterMessage}
            framePadding={0}
            $rows={5}
            isScheduleMessage
          />
          <AttachmentsContainer>
            <AttachmentButtonsContainer>
              <FileUploadButton handleFiles={onAddAttachments}>
                <StyledFakeButton>
                  <FileAttachmentIcon />
                  {texts.attachFile}
                </StyledFakeButton>
              </FileUploadButton>
              {isVoiceMemoRecording
                ? (
                  <StyledButton onClick={stopVoiceRecording}>
                    <TimeCounter run />
                    <StyledStopIcon />
                  </StyledButton>
                )
                : (
                  <StyledButton onClick={startVoiceRecording}>
                    <StyledMicIcon />
                    {texts.recordAudio}
                  </StyledButton>
                )}
            </AttachmentButtonsContainer>
            <ImageDropzone
              handleFiles={onAddAttachments}
              multiple
            >
              {currentChannelAttachments.length !== 0 && (
              <>
                {attachmentsReady
                  ? (
                    <AttachmentsPreview
                      attachments={currentChannelAttachments}
                      onRemove={onRemoveAttachment}
                      onAddAttachments={onAddAttachments}
                      isScheduleMessage
                    />
                  )
                  : (
                    <CenterLoading>
                      <CircularProgress
                        size={32}
                      />
                    </CenterLoading>
                  )}
              </>
              )}
            </ImageDropzone>
          </AttachmentsContainer>
        </>
      )}
      <DateSelectorContainer>
        <PickerContainer>
          <StyledText>
            {texts.selectDate}
          </StyledText>
          <DatePicker
            value={sendDate}
            onChange={(newValue) => setSendDate(newValue)}
            minDate={moment().utcOffset(timezoneOffset)}
            maxDate={moment().utcOffset(timezoneOffset).add(3, 'months')}
            renderInput={(params) => (
              <StyledTextField {...params} />
            )}
          />
        </PickerContainer>
        <PickerContainer>
          <StyledText>
            {texts.selectTime}
          </StyledText>
          <TimePicker
            value={sendTime}
            onChange={(newValue) => setSendTime(newValue)}
            minTime={minTime}
            minutesStep={5}
            renderInput={(params) => (
              <StyledTextField {...params} />
            )}
          />
        </PickerContainer>
      </DateSelectorContainer>

      {!!coachTimezone && (
        <Alert severity="warning">
          {texts.scheduleWarningMessage}
        </Alert>
      )}
      <CheckBoxContainer>
        <Checkbox
          checked={shouldForceSend}
          onChange={() => setShouldForceSend((prev) => !prev)}
        />
        <FormHelperText>{texts.forceSendLabel}</FormHelperText>
      </CheckBoxContainer>

      <ConfirmButtonsContainer>
        {isQueueMode ? (
          <ScheduleButton
            onClick={handleSaveAll}
            disabled={scheduleQueue.length === 0}
          >
            {scheduleAllButtonLabel}
          </ScheduleButton>
        ) : (
          <ScheduleButton
            onClick={handleSave}
            disabled={
              (text.trim().length === 0 && currentChannelAttachments.length === 0)
              || (defaultAttachments.length > 0 && !attachmentsReady)
            }
          >
            {saveButtonLabel}
          </ScheduleButton>
        )}
        {onCancel && (
          <ScheduleButton
            onClick={handleCancel}
            $light
          >
            {cancelButtonLabel}
          </ScheduleButton>
        )}
      </ConfirmButtonsContainer>
      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

FormScheduleMessage.propTypes = {
  defaultText: PropTypes.string,
  defaultSendDate: PropTypes.any,
  defaultSendTime: PropTypes.any,
  defaultAttachments: PropTypes.arrayOf(attachmentShape),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  saveButtonLabel: PropTypes.node.isRequired,
  cancelButtonLabel: PropTypes.node,
};

FormScheduleMessage.defaultProps = {
  defaultText: '',
  defaultSendDate: undefined,
  defaultSendTime: undefined,
  defaultAttachments: [],
  onCancel: undefined,
  cancelButtonLabel: '',
};

export default compose(
  withMessageInputContextProvider,
)(FormScheduleMessage);
